import { graphql, useFragment } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { styled } from '@compiled/react';
import type { LoomVideo } from '@loomhq/record-sdk';

import IconButton from '@atlaskit/button/icon/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Tooltip from '@atlaskit/tooltip/Tooltip';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { OpenLoomRecorderParams } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType.ts';
import { LOOM_PAGE_HEADER_CONTENT_INSERTION } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useLoomInsertUrlState } from '@confluence/loom-insert-url-store';
import { useShouldShowLoomSpotlight } from '@confluence/loom-utils/entry-points/useShouldShowLoomSpotlight.ts';
import { LoomSpotlightWrapper } from '@confluence/loom-utils/entry-points/LoomSpotlightWrapper.ts';

import { LoomVideoIcon } from './VideoIcon.tsx';
import type {
	PageHeaderLoomButtonQuery$data,
	PageHeaderLoomButtonQuery$variables,
} from './__generated__/PageHeaderLoomButtonQuery.graphql.ts';
import type { PageHeaderLoomButton_contentContributors$key } from './__generated__/PageHeaderLoomButton_contentContributors.graphql.ts';
import type {
	PageHeaderLoomButtonContentQuery$data,
	PageHeaderLoomButtonContentQuery$variables,
} from './__generated__/PageHeaderLoomButtonContentQuery.graphql.ts';

type PageHeaderLoomButtonComponentProps = {
	contributorsFragmentRef?: PageHeaderLoomButton_contentContributors$key;
	contentId: string;
	dataVC?: string;
	label: string;
	spaceKey: string;
	contentType: string;
	flags: FlagsStateContainer;
	ssrPlaceholderIdProp?: ReturnType<typeof useSSRPlaceholderReplaceIdProp>;
	isCircle?: boolean;
	testId?: string;
	source?: string;
	isDisabled?: boolean;
};

export enum PostLoomRecordingCTA {
	EDITOR_INSERT = 'editorInsert',
	PAGE_COMMENT_INSERT = 'pageCommentInsert',
	COPY_LINK = 'copyLink',
	OPEN_ON_LOOM = 'openOnLoom',
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const PageHeaderLoomButtonComponent: FC<
	PageHeaderLoomButtonComponentProps & {
		contentData?: NonNullable<
			NonNullable<PageHeaderLoomButtonQuery$data['content']>['nodes']
		>[number];
		isCurrentUserContributor?: boolean | null;
	}
> = ({
	contentId,
	dataVC,
	label,
	spaceKey,
	contentType,
	flags,
	ssrPlaceholderIdProp,
	isCircle = false,
	testId,
	source = 'pageHeader',
	isDisabled = false,
	contentData,
	isCurrentUserContributor,
}) => {
	const { userId } = useSessionData();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { loadEditor } = useEditPageLoadingActions();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, loomInsertUrlActions] = useLoomInsertUrlState();
	const pageMode = useGetPageMode();
	const { shouldShowSpotlight, setSeenSpotlight } = useShouldShowLoomSpotlight();

	const saveLoomUrlAndLoadEditor = (video: LoomVideo) => {
		loomInsertUrlActions.updateFullPageEditorLoomUrl(video);
		const redirectUrl = getUrlForContentType({
			contentType,
			spaceKey,
			contentId,
			redirectToEditor: true,
		});
		loadEditor({
			contentId,
			spaceKey,
			redirectUrl,
			contentType,
		});
	};

	const beginExperienceTracker = (postLoomRecordingCTA: PostLoomRecordingCTA) => {
		experienceTracker.start({
			name: LOOM_PAGE_HEADER_CONTENT_INSERTION,
			timeout: ExperienceTimeout.LOOM_PAGE_HEADER_CONTENT_INSERTION,
			attributes: { postLoomRecordingCTA },
		});
	};

	let openLoomRecorderParams: OpenLoomRecorderParams | undefined;
	let postLoomRecordingCTA: PostLoomRecordingCTA = PostLoomRecordingCTA.OPEN_ON_LOOM;
	let resetInsertFunctionOnUnmount = false;

	const isContentContributor =
		isCurrentUserContributor ||
		// isCurrentUserContributor is false for unpublished drafts, so we also check the owner
		(contentData?.history?.ownedBy &&
			'accountId' in contentData.history.ownedBy &&
			contentData.history.ownedBy.accountId === userId);

	const canEdit = contentData?.operations?.some(
		(operation) => operation?.operation === 'update' && operation?.targetType === contentType,
	);
	const canComment = contentData?.operations?.some(
		(operation) => operation?.targetType === 'comment' && operation?.operation === 'create',
	);

	if (canEdit && isContentContributor && contentType !== 'database' && contentType !== 'slide') {
		// editor mode insert
		postLoomRecordingCTA = PostLoomRecordingCTA.EDITOR_INSERT;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		};
	} else if (canComment && contentType !== 'database' && contentType !== 'slide') {
		// page comment insert
		postLoomRecordingCTA = PostLoomRecordingCTA.PAGE_COMMENT_INSERT;
		// Set to true so that CTA 'Add to comment' will not render if user has navigated to
		// different page post Loom recording.
		resetInsertFunctionOnUnmount = true;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				loomInsertUrlActions.updatePageCommentEditorLoomUrl(video);
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				const openPageCommentEditorButton = document.getElementById('addCommentButton');
				openPageCommentEditorButton && openPageCommentEditorButton.click();
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Add to comment',
		};
	} else {
		postLoomRecordingCTA = PostLoomRecordingCTA.COPY_LINK;
		// Not defining openLoomRecorderParams here so that
		// undefined will be passed to the Loom SDK resulting
		// in the Loom SDK rendering a single CTA of 'Copy link'
	}

	const { openLoomRecorder, isOpeningRecorder, isLoomRecorderInitialized } =
		useLoomRecorderEntryPoint({
			entryPointLocation: 'pageHeader',
			flags,
			resetInsertFunctionOnUnmount,
		});

	// This button is visible (and SSR'd) before the Loom SDK has initialized. Like the other Loom
	// entry points, disable it until the SDK is ready instead of letting a click hit the hook's
	// "SDK has not been configured yet" abort.
	const isButtonDisabled =
		isDisabled ||
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		(!isLoomRecorderInitialized && fg('confluence_page_header_loom_button_wait_for_sdk_init'));

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createLoomHeaderItem',
				source,
				attributes: {
					postLoomRecordingCTA,
					contentType,
					pageMode,
				},
			},
		}).fire();

		void openLoomRecorder(openLoomRecorderParams);
		if (shouldShowSpotlight) {
			setSeenSpotlight();
		}
	}, [
		createAnalyticsEvent,
		openLoomRecorder,
		openLoomRecorderParams,
		postLoomRecordingCTA,
		source,
		pageMode,
		contentType,
		shouldShowSpotlight,
		setSeenSpotlight,
	]);

	if (shouldShowSpotlight) {
		// Render with Spotlight wrapper when shouldShowSpotlight is true
		const baseLoomIconButton = isCircle ? (
			<IconButton
				label={label}
				isLoading={isOpeningRecorder}
				onClick={handleClick}
				appearance="subtle"
				shape="circle"
				icon={LoomVideoIcon}
				testId={testId}
				isDisabled={isButtonDisabled}
			/>
		) : (
			<IconButton
				appearance="subtle"
				icon={LoomVideoIcon}
				label={label}
				onClick={handleClick}
				isLoading={isOpeningRecorder}
				data-vc={dataVC}
				{...ssrPlaceholderIdProp}
				testId={testId}
				isDisabled={isButtonDisabled}
			/>
		);

		return (
			<LoomSpotlightWrapper
				onSpotlightPrimaryActionClick={handleClick}
				onSpotlightClose={setSeenSpotlight}
				touchpoint="page-header"
			>
				<ButtonSpacer>{baseLoomIconButton}</ButtonSpacer>
			</LoomSpotlightWrapper>
		);
	}

	// Default render with Tooltip
	const baseLoomButton = (
		<Tooltip content={label} position="left">
			{(tooltipProps) => (
				<ButtonSpacer>
					{isCircle ? (
						<IconButton
							label={label}
							{...tooltipProps}
							isLoading={isOpeningRecorder}
							onClick={handleClick}
							appearance="subtle"
							shape="circle"
							icon={LoomVideoIcon}
							testId={testId}
							isDisabled={isButtonDisabled}
						/>
					) : (
						<IconButton
							appearance="subtle"
							icon={LoomVideoIcon}
							label={label}
							{...tooltipProps}
							onClick={handleClick}
							isLoading={isOpeningRecorder}
							data-vc={dataVC}
							{...ssrPlaceholderIdProp}
							testId={testId}
							isDisabled={isButtonDisabled}
						/>
					)}
				</ButtonSpacer>
			)}
		</Tooltip>
	);

	return baseLoomButton;
};

const PageHeaderLoomButtonFromQuery: FC<PageHeaderLoomButtonComponentProps> = (props) => {
	const { contentId } = props;
	const { data } = useQueryInterop<
		PageHeaderLoomButtonQuery$data,
		PageHeaderLoomButtonQuery$variables
	>(
		graphql`
			query PageHeaderLoomButtonQuery($contentId: ID!) {
				contentContributors(id: $contentId) {
					isCurrentUserContributor
				}
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						operations {
							operation
							targetType
						}
						history {
							ownedBy {
								... on KnownUser {
									accountId
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);

	return (
		<PageHeaderLoomButtonComponent
			{...props}
			contentData={data?.content?.nodes?.[0]}
			isCurrentUserContributor={data?.contentContributors?.isCurrentUserContributor}
		/>
	);
};

const PageHeaderLoomButtonFromFragment: FC<
	PageHeaderLoomButtonComponentProps & {
		contributorsFragmentRef: PageHeaderLoomButton_contentContributors$key;
	}
> = (props) => {
	const contributors = useFragment(
		graphql`
			fragment PageHeaderLoomButton_contentContributors on ContentContributors {
				isCurrentUserContributor
			}
		`,
		props.contributorsFragmentRef,
	);
	if (contributors.isCurrentUserContributor == null)
		return <PageHeaderLoomButtonFromQuery {...props} />;
	return (
		<PageHeaderLoomButtonWithContent
			{...props}
			isCurrentUserContributor={contributors.isCurrentUserContributor}
		/>
	);
};

const PageHeaderLoomButtonWithContent: FC<
	PageHeaderLoomButtonComponentProps & { isCurrentUserContributor: boolean }
> = (props) => {
	const { contentId } = props;
	const { data } = useQueryInterop<
		PageHeaderLoomButtonContentQuery$data,
		PageHeaderLoomButtonContentQuery$variables
	>(
		graphql`
			query PageHeaderLoomButtonContentQuery($contentId: ID!) {
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						operations {
							operation
							targetType
						}
						history {
							ownedBy {
								... on KnownUser {
									accountId
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);

	return <PageHeaderLoomButtonComponent {...props} contentData={data?.content?.nodes?.[0]} />;
};

const PageHeaderLoomButtonWithData: FC<PageHeaderLoomButtonComponentProps> = (props) => {
	if (props.contentId && props.contributorsFragmentRef) {
		return (
			<PageHeaderLoomButtonFromFragment
				{...props}
				contributorsFragmentRef={props.contributorsFragmentRef}
			/>
		);
	}
	return <PageHeaderLoomButtonFromQuery {...props} />;
};

export const PageHeaderLoomButton: React.ComponentType<
	Omit<PageHeaderLoomButtonComponentProps, 'flags'>
> = withFlags(PageHeaderLoomButtonWithData);
